/**
 * De stapelvolgorde van de bewonerstablet, op één plek.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/plan.md §6.4
 * Taak: T-2.01
 *
 * ── WAAROM DIT BESTAND BESTAAT ──────────────────────────────────────────────
 *
 * De getallen stonden verspreid over zeventien bestanden, elk met een los cijfer in een
 * inline `style`. Dat is precies hoe `CommandOverlay` op **50** kon belanden terwijl een
 * actieve oproep op 9990 zit: niemand die dat cijfer typte had de andere zestien in beeld.
 * Het gevolg was niet theoretisch — een `show_message`-popup verdween achter de
 * screensaver, en op een wandtablet zonder toetsenbord is dat een melding die niemand ooit
 * ziet.
 *
 * Eén tabel maakt de vraag "waar komt dit bovenop?" beantwoordbaar zonder te grepen. Zet
 * hier een laag bij vóór je hem gebruikt; een los getal in een component is het defect,
 * niet de oplossing.
 *
 * ── DE REGEL DIE DE VOLGORDE BEPAALT ────────────────────────────────────────
 *
 * **Een melding gaat nooit boven een oproep en nooit boven de noodmelding.** Iemand die
 * belt of die in nood is, wint van elk bericht — hoe dringend dat bericht ook heet. Komt
 * er een oproep binnen terwijl een volledig-schermmelding staat, dan wordt die melding
 * gepauzeerd (aftelling stopt, TTS gestopt) en na de oproep hervat als hij nog geldig is.
 *
 * ── DE BANDEN ───────────────────────────────────────────────────────────────
 *
 *   99999  noodrem            — de sessie is beëindigd, niets mag daar nog overheen
 *    9999  opstart            — splash en login, vóór er iets anders bestaat
 *    9990  gesprek            — actieve oproep en videofoon
 *    9985  diagnose           — de technicusoverlay, bewust boven de meldingen
 *    9980  audio-activatie    — de banner die geluid ontgrendelt (zie de noot hieronder)
 *    9975  noodmelding
 *    9960  melding: volledig scherm
 *    9950  melding: dialoog
 *    9940  melding: legacy `show_message`
 *    9970  paginabreed scherm — bestaande volledig-schermpanelen (zie de noot)
 *    9000  stand-by           — screensaver, dimlaag, slaapstand
 *     <100 paginainhoud
 *
 * ⚠ `FULLSCREEN_PAGE` (9970) staat hoger dan de stand-bylaag en is daarmee de enige
 * paginainhoud die de slaapstand overleeft. Dat is BESTAAND gedrag (die panelen stonden op
 * 9970 terwijl de slaapstand op 60 stond) en het is hier bewust niet gerepareerd: dat is
 * een gedragswijziging die niet in deze fase thuishoort. Het staat genoteerd zodat de
 * volgende lezer het ziet in plaats van het opnieuw te ontdekken.
 *
 * ⚠ `AUDIO_ACTIVATION_BANNER` (9980) is GERESERVEERD, niet in gebruik. De interimbanner uit
 * de ringtone-mitigatie van 2026-07-23 is met de terugrol van 2026-09-01 uit de app
 * verdwenen; alleen een commentaarregel in `i18n/types.ts` herinnert eraan. Het getal
 * blijft staan zodat de banner terug kan komen op zijn eigen plek in plaats van op een
 * nieuw verzonnen cijfer.
 *
 * ── WAT HIER NIET IN HOORT ──────────────────────────────────────────────────
 *
 * Lokale stapeling BINNEN één component — een kaart in een spel, een label boven een
 * achtergrondafbeelding, `zIndex: 1` op een tekstlaag — is geen laag van de app en blijft
 * lokaal. Deze tabel gaat over wie er over wie heen valt op het volledige scherm.
 */

export const Z_INDEX = {
  /** Sessie beëindigd op een ander toestel. Niets mag hier nog overheen. */
  SESSION_CONFLICT: 99999,

  /** Opstartsplash en het loginscherm. */
  BOOT: 9999,

  /** Actieve oproep, inkomende oproep, videofoon. Wint van elke melding. */
  CALL: 9990,

  /** De diagnostiekoverlay voor een technicus, en zijn openknop. */
  DEBUG_OVERLAY_PANEL: 9986,
  DEBUG_OVERLAY_TOGGLE: 9985,

  /** Gereserveerd — zie de noot in de kop. */
  AUDIO_ACTIVATION_BANNER: 9980,

  /** Noodmelding. Alleen een oproep staat hierboven. */
  EMERGENCY: 9975,

  /** Bestaande volledig-schermpanelen binnen een pagina (domotica, agenda, editor). */
  FULLSCREEN_PAGE: 9970,

  /** Melding in volledig scherm (fase 3). */
  NOTIFICATION_FULLPAGE: 9960,

  /** Melding als dialoog (fase 2). */
  NOTIFICATION_DIALOG: 9950,

  /**
   * Het vangnet voor `show_message`.
   *
   * ⚠ Stond op 50 en dus ONDER de screensaver. Dat blok blijft verder ongewijzigd: het
   * bedient 800+ tablets met gemengde bundles en is het enige pad voor een oudere app die
   * `resident_notification` nog niet kent.
   */
  COMMAND_OVERLAY: 9940,

  /**
   * De stand-bylaag.
   *
   * Drie componenten, oplopend in dezelfde volgorde als voorheen (screensaver < dim <
   * slaap), zodat de onderlinge verhouding ongewijzigd blijft. Alles hierboven — dus élke
   * melding — is per definitie zichtbaar door de dimlaag en over de screensaver heen.
   */
  SCREENSAVER: 9000,
  DIM_OVERLAY: 9005,
  SLEEP_OVERLAY: 9010,

  /** Een modaal venster binnen een pagina (fotodetail). Onder de stand-bylaag. */
  PAGE_MODAL: 60,

  /** Zwevende paginaonderdelen. */
  BATTERY_WARNING: 45,
  WIDGET_GRID_OVERLAY: 41,
  EMERGENCY_BUTTON: 40,
} as const;

export type ZIndexLayer = keyof typeof Z_INDEX;
